import { timezoneMapping } from '@components/time-zone/TimeZoneMapping';
import { appConfig } from '@configs/index';
import { ContraventionStatus } from '@EcamModel/model';
import { Schedule } from '@EcamModel/model/PTZAutoTracking';
import { ScheduleItem } from '@EcamModel/model/PTZScheduledTask';
import { Divider, Stack, Typography } from '@mui/material';
import { DataSectionHasSubItem } from '@pages/cameras/details/components/tabsGeozonesDetail/ptz/milesight/components/BasicInfoMilesightCameraPTZ';
import color from '@theme/Colors';
import _, * as lodash from 'lodash';
import moment, { Moment } from 'moment';
import { ReactNode } from 'react';

export default function GetBooleanFromEnv(string: string) {
    return string === 'true';
}

export const formatActivateTime = (date: Date) => {
    return moment(date).fromNow();
};

export const formatLocaleTime = (date?: Date) => {
    if (!date) return 'Not available';
    return moment(date).tz(appConfig.timeZone).format(`DD/MM/YYYY HH:mm:ss`);
};

export const formatUKDateTime = (date?: moment.MomentInput) => {
    if (!date) return 'Not available';
    return moment(date).tz(appConfig.timeZone).format('DD/MM/YYYY HH:mm:ss');
};

export const formatDateByTimeZone = (date: moment.MomentInput, timezone?: string, formatSS?: boolean) => {
    const _format: string = formatSS === false ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY HH:mm:ss';
    return moment(date)
        .tz(timezone ?? appConfig.timeZone)
        .format(_format);
};

export const formatUKDate = (date: moment.MomentInput, timezone?: string) => {
    return moment(date).tz(timezone ?? appConfig.timeZone).format('DD/MM/YYYY');
};

export const formatUKTime = (date: moment.MomentInput, timezone?: string) => {
    return moment(date).tz(timezone ?? appConfig.timeZone).format('HH:mm:ss');
};

export const formatISODate = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('YYYY-MM-DD');
};

export const formatISODateTime = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('YYYY-MM-DD HH:mm:ss');
};

export const formatUKDateDash = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('DD-MM-YYYY');
};

export const formatUKDateTimeShort = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('DD MMM YYYY, HH:mm');
};

export const formatUKDayMonth = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('DD/MM');
};

export const formatDateTime = (date: Date) => {
    return moment(date).tz(appConfig.timeZone).format('DD/MM/YYYY HH:mm:ss');
};

export const startOfDay = (date: Date | string): moment.Moment => {
    return moment(date).startOf('day');
};

export const getSecond = (date: moment.Moment) => {
    return date.hours() * 60 * 60 + date.minutes() * 60;
};

export const getFirstAndLastDayOfCurrentMonth = () => {
    const currentDate = moment().tz(appConfig.timeZone);

    const firstDayOfMonth = currentDate.clone().startOf('month');
    const lastDayOfMonth = currentDate.clone().endOf('month');

    return {
        from: firstDayOfMonth.toDate(),
        to: lastDayOfMonth.toDate(),
    };
};
export const toUTCStartOfDay = (date?: Date) => {
    return date ? moment(date).tz(appConfig.timeZone).startOf('day').toDate() : undefined;
};

export const toUTCEndOfDay = (date?: Date) => {
    return date ? moment(date).tz(appConfig.timeZone).endOf('day').toDate() : undefined;
};

export const statusMap: Record<ContraventionStatus, string> = {
    [ContraventionStatus.Open]: 'Open',
    [ContraventionStatus.Paid]: 'Paid',
    [ContraventionStatus.Cancelled]: 'Cancelled',
    [ContraventionStatus.Timeout]: 'Timeout',
    [ContraventionStatus.Paused]: 'Paused',
    [ContraventionStatus.Approved]: 'Approved',
    [ContraventionStatus.InProgress]: 'In Progress',
    [ContraventionStatus.SystemCancelled]: 'System cancelled',
};

export const startOfYesterday = moment().tz('Europe/London').subtract(1, 'day').startOf('day').toDate();
export const endOfYesterday = moment().tz('Europe/London').subtract(1, 'day').endOf('day').toDate();

export const startOfThreeDaysAgo = moment().tz('Europe/London').subtract(3, 'days').startOf('days').toDate();
export const endOfStartDate = moment(startOfYesterday).tz('Europe/London').endOf('days').toDate();

export const getFirstAndLastDayOfCurrentWeek = () => {
    const currentDate = moment();

    const firstDayOfWeek = currentDate.clone().startOf('week');
    const lastDayOfWeek = currentDate.clone().endOf('week');

    return {
        fromCurrentWeek: firstDayOfWeek.toDate(),
        toCurrentWeek: lastDayOfWeek.toDate(),
    };
};

export const formatUKTimeDate = (date?: Date) => {
    if (!date) return 'Not available';
    return moment(date).tz(appConfig.timeZone).format('HH:mm DD/MM/YYYY');
};

export const disableFutureDates = (date: Date) => {
    return moment(date).isAfter(moment(), 'day');
};

export const formatTimeString = (startTime: Date, EndTime: Date) => {
    return lodash.upperFirst(moment.duration(moment(startTime).diff(moment(EndTime))).humanize());
};

export const isSameDay = (date1: moment.MomentInput, date2: moment.MomentInput) =>
    moment(date1).isSame(moment(date2), 'day');

export function formatMinutesToDurationString(minutes) {
    const duration = moment.duration(Math.abs(minutes), 'minutes');
    const humanizedDuration = lodash.upperFirst(duration.humanize(minutes < 0));
    return humanizedDuration;
}

export function generateUniqueColors(arrayLength: number) {
    const uniqueColors = new Set();

    while (uniqueColors.size < arrayLength) {
        const color =
            '#' +
            Math.floor(Math.random() * 16777215)
                .toString(16)
                .padStart(6, '0');
        uniqueColors.add(color);
    }

    return Array.from(uniqueColors);
}

export const formatTimeFromSeconds = (seconds: number): string => {
    const hours = _.floor(seconds / 3600);
    const minutes = _.floor((seconds % 3600) / 60);
    return `${_.padStart(hours.toString(), 2, '0')}:${_.padStart(minutes.toString(), 2, '0')}`;
};

export const formatNumber = (amount: number) => {
    let formattedNumber = new Intl.NumberFormat('en-US', {
        style: 'decimal',
    }).format(amount);
    return formattedNumber;
};

export const formatTruncateDecimal = (value: number, decimalPlaces: number) => {
    let decimalIndex = value.toString().indexOf('.');
    if (decimalIndex === -1) {
        return value.toString();
    }
    return parseFloat(value.toString().slice(0, decimalIndex + 1 + decimalPlaces));
};

export const storedCameraColors: Map<number, string> = localStorage.getItem('cameraChartLineColors')
    ? new Map<number, string>(JSON.parse(localStorage.getItem('cameraChartLineColors')!))
    : new Map<number, string>();

export const saveCameraColorsToLocalStorage = (colorMap: Map<number, string>) => {
    localStorage.setItem('cameraChartLineColors', JSON.stringify(Array.from(colorMap.entries())));
};

export const getRandomUnusedColor = (usedColors: Set<string>): string => {
    let color: string;
    do {
        const getRandomHex = () =>
            Math.floor(Math.random() * 256)
                .toString(16)
                .padStart(2, '0');
        const r = getRandomHex();
        const g = getRandomHex();
        const b = getRandomHex();
        color = `#${r}${g}${b}`;
    } while (usedColors.has(color));

    usedColors.add(color);
    return color;
};

export const adjustPreviewSize = (imageWidth: number, imageHeight: number, width: number, height: number) => {
    const aspectRatio = imageWidth / imageHeight;
    let previewWidth = width;
    let previewHeight = height;

    if (aspectRatio > width / height) {
        previewWidth = width;
        previewHeight = width / aspectRatio;
    } else {
        previewHeight = height;
        previewWidth = height * aspectRatio;
    }

    return { previewWidth, previewHeight };
};

export const convertToDMS = (lat: number, lng: number) => {
    const toDMS = (coordinate: number, isLat: boolean) => {
        const absolute = Math.abs(coordinate);
        const degrees = Math.floor(absolute);
        const minutes = Math.floor((absolute - degrees) * 60);
        const seconds = ((absolute - degrees) * 60 - minutes) * 60;

        const direction = coordinate >= 0 ? (isLat ? 'N' : 'E') : isLat ? 'S' : 'W';

        return `${degrees}°${minutes}'${seconds.toFixed(1)}"${direction}`;
    };

    const formatDecimal = (num: number, precision: number) => {
        return num.toFixed(precision);
    };

    const latDMS = toDMS(lat, true);
    const lngDMS = toDMS(lng, false);

    return {
        latDMS,
        lngDMS,
        latDecimal: formatDecimal(lat, 6),
        lngDecimal: formatDecimal(lng, 6),
    };
};

export const formatTimeDayHourMinute = (seconds: number): string => {
    const duration = moment.duration(seconds, 'seconds');
    const years = duration.years();
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    const parts = [
        { value: years, unit: 'y' },
        { value: days, unit: 'd' },
        { value: hours, unit: 'h' },
        { value: minutes, unit: 'm' },
    ];

    return parts
        .filter((part) => part.value > 0)
        .map((part) => `${part.value}${part.unit}`)
        .join(' ')
        .trim();
};

export const getMinutesFromStartOfDay = (date: Date) => {
    const midnight = moment(date).startOf('day');
    const minutesSinceMidnight = moment(date).diff(midnight, 'minutes');
    return minutesSinceMidnight;
};

export const formatMinutesToTime = (minutes: number): Moment => {
    return moment().startOf('day').add(minutes, 'minutes');
};

export const capitalizeFirstCharacter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export const filterUndefinedParams = (params: { [key: string]: any }) => {
    return Object.keys(params).reduce((acc, key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            acc[key] = params[key];
        }
        return acc;
    }, {} as { [key: string]: any });
};

export const getTimezoneDescription = (timezone: string): string => {
    return timezoneMapping[timezone] || 'Unknown Timezone';
};

export const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

enum Task {
    Close,
    AutoScan,
    Preset,
    Patrol,
    Pattern,
    Check,
    TiltScan,
    PanoramaScan,
}

export const taskTypeMapping: { [key: number]: string } = {
    [Task.Close]: 'Close',
    [Task.AutoScan]: 'Auto Scan',
    [Task.Preset]: 'Preset',
    [Task.Patrol]: 'Patrol',
    [Task.Pattern]: 'Pattern',
    [Task.Check]: 'Check',
    [Task.TiltScan]: 'Tilt Scan',
    [Task.PanoramaScan]: 'Panorama Scan',
};

export function formatTaskSchedule(schedule: string): ReactNode {
    if (schedule?.trim() === '') {
        return (
            <Stack direction="row" width={'100%'}>
                <Typography variant="body2" width={'100%'}>
                    &#8212;
                </Typography>
            </Stack>
        );
    }

    const tasks = schedule?.split(';').filter((task) => task.trim() !== '');

    const formattedTasks = tasks?.map((task, index) => {
        const parts = task?.split(':');
        if (parts?.length === 4) {
            const startTime = `${parts[0]}:${parts[1]}`;
            const stopTime = `${parts[2]}:${parts[3]}`;
            return `${startTime} - ${stopTime}`;
        } else if (parts?.length === 6) {
            const startTime = `${parts[0]}:${parts[1]}`;
            const stopTime = `${parts[2]}:${parts[3]}`;
            const type = taskTypeMapping[Number(parts[4])] || 'Unknown Type';
            // const id = parts[5];
            return (
                <Stack key={index} direction="row" width={'100%'} mb={'10px'}>
                    <Typography variant="body2" color={color.grey600} width={'27%'} mt={'2px'}>
                        {startTime} - {stopTime}
                    </Typography>

                    <Divider
                        orientation="horizontal"
                        sx={{
                            borderRightWidth: 1,
                            borderColor: '#E4E7EC',
                            marginLeft: '4px',
                            marginRight: '16px',
                        }}
                    />
                    <Typography variant="body1" color={color.textPrimary} width={'max-content'}>
                        {type}
                    </Typography>
                </Stack>
            );
        } else {
            return (
                <Stack key={index} direction="row" width={'100%'}>
                    <Typography variant="body1" color={color.textPrimary} width={'100%'}>
                        Invalid Format
                    </Typography>
                </Stack>
            );
        }
    });

    return (
        <>
            <Typography variant="body1">
                {formattedTasks?.filter((task) => typeof task === 'string').join(', ')}
            </Typography>
            {formattedTasks?.filter((task) => typeof task !== 'string')}
        </>
    );
}

export const transformScheduleData = <T extends DataSectionHasSubItem>(
    apiData: ScheduleItem[] | Schedule[],
    mapFn: (dayName: string, scheduleComponent: ReactNode) => T
): T[] => {
    return apiData?.map((item) => {
        const dayName = daysOfWeek[item.ScheduleIndex];
        const scheduleComponent = formatTaskSchedule(item.Schedule);
        return mapFn(dayName, scheduleComponent);
    });
};
